import { NBXStorage } from '@nbx/frontend-helpers/storage';
// eslint-disable-next-line import/no-cycle
import { post } from '@nbx/frontend-helpers/fetch-api';
import { getAuthBaseUrl } from './url';
import { Claims, Session } from '@nbx/frontend-helpers/types';
// eslint-disable-next-line import/no-cycle
import { getSession, publishSessionExpired } from './session';
import { isSessionRejected, wait, TRANSIENT_RETRY_DELAYS_MS } from './refreshFailure';

export interface UserTokenResponse {
  claims: Claims;
  exp: number;
  token: string;
}

export const clearGlobalFetchUserTokenPromise = (): void => {
  window.fetchUserTokenPromise = null;
};

export async function refreshUserToken(session: Session): Promise<string> {
  if (window.fetchUserTokenPromise) {
    console.log(
      `[helpers.refreshUserToken] return cached fetchUserTokenPromise (session.exp=${session?.exp})`
    );
    setTimeout(clearGlobalFetchUserTokenPromise, 15000);
    return window.fetchUserTokenPromise;
  }
  window.fetchUserTokenPromise = doRefreshUserToken(session);
  return window.fetchUserTokenPromise;
}

let userTokenRefreshTimeout: ReturnType<typeof setTimeout>;

export const setUserTokensDebounceRefresh = (response: UserTokenResponse): void => {
  if (userTokenRefreshTimeout) clearTimeout(userTokenRefreshTimeout);
  userTokenRefreshTimeout = setTimeout(
    () =>
      getSession()
        .then(refreshUserToken)
        .catch(e => console.warn('setUserTokensDebounceRefresh: cannot getSession and refreshUserToken', e)),
    (response.claims.exp - response.claims.iat) * 1000 * 0.65
  );
};

export async function doRefreshUserToken(session: Session, attempt = 0): Promise<string> {
  try {
    if (!session) {
      console.log(`[helpers.doRefreshUserToken] cannot refresh user token: no session`);
      throw new Error('doRefreshUserToken: no session');
    }
    const response = (await post(
      `${getAuthBaseUrl()}/users/${session.userId}/tokens`,
      { json: {} },
      session
    )) as UserTokenResponse;
    await NBXStorage.setItem(session.userId, response.token);
    setUserTokensDebounceRefresh(response);
    setTimeout(clearGlobalFetchUserTokenPromise, 15000);
    return response.token;
  } catch (e) {
    if (session && !isSessionRejected(e)) {
      if (attempt < TRANSIENT_RETRY_DELAYS_MS.length) {
        console.log(
          `[helpers.doRefreshUserToken] no answer from the server, retry ${attempt + 1}/${TRANSIENT_RETRY_DELAYS_MS.length}`,
          e
        );
        await wait(TRANSIENT_RETRY_DELAYS_MS[attempt]);
        return doRefreshUserToken(session, attempt + 1);
      }
      // Session kept: the next 401 refreshes on demand, so there is no timer to re-arm here.
      console.warn(`doRefreshUserToken: giving up for now, session kept (session.exp=${session?.exp})`, e);
      clearGlobalFetchUserTokenPromise();
      throw e;
    }
    console.warn(
      `doRefreshUserToken: will logout - session rejected while refreshing user token using session.exp=${session?.exp}`
    );
    clearGlobalFetchUserTokenPromise();
    if (window.PLATFORM === 'web') {
      window.LOGOUT();
    } else {
      publishSessionExpired();
    }
    throw e;
  }
}
